import React from 'react';
import { CheckCircle2, Clock, Wrench } from 'lucide-react';
import PageLayout, { PageHero } from '../components/PageLayout';

const SYSTEMS = [
  { name: 'WASLX web dashboard',      status: 'operational', latency: '112ms' },
  { name: 'Android agent app',        status: 'operational', latency: '—' },
  { name: 'Auto dialer engine',       status: 'operational', latency: '84ms' },
  { name: 'Lead management API',      status: 'operational', latency: '98ms' },
  { name: 'Campaign management',      status: 'operational', latency: '101ms' },
  { name: 'Live monitoring dashboard', status: 'operational', latency: '76ms' },
  { name: 'Call recording storage',   status: 'operational', latency: '230ms' },
  { name: 'Push notifications',       status: 'operational', latency: '145ms' },
  { name: 'Analytics & reports',      status: 'operational', latency: '188ms' },
];

const UPTIME = [
  { label: 'Uptime (30 days)', value: '99.98%', accent: 'accent-4' },
  { label: 'Avg response',     value: '128ms',  accent: 'accent-6' },
  { label: 'Incidents (30d)',  value: '0',      accent: 'accent-1' },
];

const HISTORY = [
  { date: 'Apr 27, 2025', event: 'All systems nominal. No incidents.', type: 'ok' },
  { date: 'Apr 26, 2025', event: 'All systems nominal. No incidents.', type: 'ok' },
  { date: 'Apr 25, 2025', event: 'All systems nominal. No incidents.', type: 'ok' },
  { date: 'Apr 24, 2025', event: 'Scheduled maintenance — dashboard (02:00–02:30 IST). No user impact.', type: 'maintenance' },
  { date: 'Apr 23, 2025', event: 'All systems nominal. No incidents.', type: 'ok' },
];

/* Status is meaning, so it takes the semantic chip pair. */
const STATUS_CHIP = {
  operational: 'chip--success',
  degraded:    'chip--warning',
  outage:      'chip--danger',
};

const SystemStatus = () => (
  <PageLayout
    title="System Status — All Services Operational"
    description="Live WASLX platform status — uptime for the web dashboard, Android app, auto dialer engine, lead management API, call recordings, and real-time analytics."
    path="/status"
  >
    <PageHero
      eyebrow="System status"
      accent="accent-4"
      title={
        <>
          <span className="display-accent">All systems</span> operational
        </>
      }
      sub="Live health for every WASLX service. This page reflects the last automated check."
    >
      <span className="chip chip--success mt-6">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Last checked: just now
      </span>
    </PageHero>

    <section className="section section--muted">
      <div className="container-page">
        <div className="mx-auto max-w-3xl space-y-10">
          <div>
            <h2 className="mb-4 text-xl font-bold text-content">Services</h2>
            <div className="overflow-hidden rounded-lg bg-surface shadow-soft">
              {SYSTEMS.map(system => (
                <div
                  key={system.name}
                  className="flex items-center justify-between gap-4 border-b border-line px-5 py-4 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-success" />
                    <span className="text-[0.9375rem] text-content">{system.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    {system.latency !== '—' && (
                      <span className="hidden font-mono text-sm tabular-nums text-content-muted sm:block">
                        {system.latency}
                      </span>
                    )}
                    <span className={`chip ${STATUS_CHIP[system.status]}`}>{system.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {UPTIME.map(item => (
              <div key={item.label} className={`${item.accent} stat-tile text-center`}>
                <div className="stat-tile__value font-mono">{item.value}</div>
                <div className="stat-tile__label">{item.label}</div>
              </div>
            ))}
          </div>

          <div>
            <h2 className="mb-4 text-xl font-bold text-content">Incident history</h2>
            <div className="space-y-2">
              {HISTORY.map(entry => (
                <div
                  key={entry.date}
                  className="flex items-start gap-3 rounded-xs bg-surface px-5 py-4 shadow-soft"
                >
                  {entry.type === 'maintenance' ? (
                    <Wrench className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" />
                  ) : (
                    <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-content-muted" />
                  )}
                  <div>
                    <span className="mr-3 font-mono text-sm tabular-nums text-content-muted">
                      {entry.date}
                    </span>
                    <span className="text-[0.9375rem] text-content">{entry.event}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  </PageLayout>
);

export default SystemStatus;
